// ============================================================================
// Update a count — the ONE component that writes a count (§15a). A count can be
// taken any time, apart from a delivery (decision 2): the AM counted it, a night
// manager or cleaner told the AM (and who), or it was taken during an
// inspection. The same window counts a STOCK LOCATION ("Correct KC count"),
// which sets that location's balance.
// ⛔ The draft resets on open and after a successful save.
// ============================================================================

import { useEffect, useState } from 'react';
import { logCount, type CountSource } from './api';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';
import { LoggingAs } from './logging-as';

export interface CountTarget {
  facilityId?: string | null;
  officeId?: string | null;
  title: string;
  amEid?: string | null;
  amName?: string | null;
  /** The clinic's cleaner, offered as "who told me". */
  providerName?: string | null;
  products: Array<{ supply_id: string; unit_label: string | null }>;
}

interface Draft {
  source: CountSource;
  counts: Record<string, string>;
  reporter: string;
  reporterIsCleaner: boolean;
  notes: string;
}

const EMPTY: Draft = { source: 'self', counts: {}, reporter: '', reporterIsCleaner: false, notes: '' };

export function UpdateCountWindow({ open, onClose, target }: { open: boolean; onClose: () => void; target: CountTarget }) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  useEffect(() => {
    if (open) setDraft(EMPTY);
  }, [open]);

  const atLocation = !!target.officeId;
  const lines = target.products
    .map((p) => ({ supply_id: p.supply_id, qty: parseQty(draft.counts[p.supply_id] ?? '') }))
    .filter((l): l is { supply_id: string; qty: number } => l.qty != null);
  const whoOk =
    draft.source !== 'reported' || draft.reporterIsCleaner || draft.reporter.trim().length > 0;
  const canSave = lines.length > 0 && whoOk;

  async function save() {
    const reporterName = draft.reporterIsCleaner ? target.providerName ?? null : draft.reporter.trim() || null;
    await logCount({
      facilityId: target.facilityId ?? null,
      officeId: target.officeId ?? null,
      counts: lines,
      source: atLocation ? 'self' : draft.source,
      reporterName: draft.source === 'reported' ? reporterName : null,
      notes: draft.notes || null,
    });
    setDraft(EMPTY);
    onClose();
  }

  return (
    <StockWindow
      open={open}
      title={atLocation ? 'Correct the count here' : 'Update a count'}
      subtitle={target.title}
      canSave={canSave}
      saveLabel="Save count"
      onCancel={onClose}
      onSave={save}
    >
      {!atLocation && <LoggingAs amEid={target.amEid ?? null} amName={target.amName ?? null} />}

      {!atLocation && (
        <fieldset>
          <legend className="mb-1 text-xs text-tcs-text-muted">Where did this count come from?</legend>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['self', 'I counted it'],
                ['reported', 'Night manager or cleaner told me'],
                ['inspection', 'During an inspection'],
              ] as Array<[CountSource, string]>
            ).map(([v, label]) => (
              <label
                key={v}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${
                  draft.source === v ? 'border-tcs-text bg-st-info-bg text-tcs-text' : 'border-tcs-border text-tcs-text-secondary'
                }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  checked={draft.source === v}
                  onChange={() => setDraft((d) => ({ ...d, source: v }))}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {!atLocation && draft.source === 'reported' && (
        <div className="space-y-2">
          {target.providerName && (
            <label className="flex items-center gap-2 text-sm text-tcs-text">
              <input
                type="checkbox"
                checked={draft.reporterIsCleaner}
                onChange={(e) => setDraft((d) => ({ ...d, reporterIsCleaner: e.target.checked }))}
              />
              The clinic’s cleaner ({target.providerName})
            </label>
          )}
          {!draft.reporterIsCleaner && (
            <label className="block">
              <span className="mb-1 block text-xs text-tcs-text-muted">Who told you?</span>
              <input
                className={FIELD_CLASS}
                placeholder="Their name"
                value={draft.reporter}
                onChange={(e) => setDraft((d) => ({ ...d, reporter: e.target.value }))}
              />
            </label>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {target.products.map((p) => (
          <QtyField
            key={p.supply_id}
            label={p.unit_label === 'tabs' ? 'Stearns, tabs' : 'Micro-Chem, gallons'}
            value={draft.counts[p.supply_id] ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, counts: { ...d.counts, [p.supply_id]: v } }))}
            step={p.unit_label === 'tabs' ? 1 : 0.1}
          />
        ))}
      </div>
      <p className="text-[11px] text-tcs-text-muted">Leave a product empty if you did not count it.</p>

      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Anything worth saying (optional)</span>
        <input className={FIELD_CLASS} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
      </label>
    </StockWindow>
  );
}
